import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button3D } from '../ui/button-3d';
import { Building2, TrendingUp, Users, Shield, ArrowRight, Sparkles } from 'lucide-react';
import { ResponsiveContainer } from '../layout/ResponsiveContainer';

const LandlordSection: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Building2,
      title: 'Property Management',
      description: 'Streamline listings, applications, and tenant communication in one powerful dashboard.'
    },
    {
      icon: TrendingUp,
      title: 'Smart Analytics',
      description: 'Track performance metrics, occupancy rates, and revenue across your entire portfolio.'
    },
    {
      icon: Users,
      title: 'Quality Tenants',
      description: 'AI-powered matching connects you with verified, qualified tenants who fit your properties.'
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'Built-in background checks, secure payments, and comprehensive tenant screening.'
    }
  ];

  const pricingTiers = [
    { range: '1-3 units', price: '$12/unit/mo' },
    { range: '4-15 units', price: '$9/unit/mo' },
    { range: '16-50 units', price: '$6/unit/mo' },
    { range: '50+ units', price: '$4-5/unit/mo' },
  ];

  return (
    <section aria-labelledby="landlord-title" className="w-full pt-20 pb-16 bg-cyan-light relative z-0">
      <ResponsiveContainer>
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/30 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Free to get started • No credit card required
          </div>
          <h2 id="landlord-title" className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Built for Modern Landlords
          </h2>
          <p className="text-xl text-slate-700 max-w-2xl mx-auto">
            Transform how you manage properties with AI-powered tools that save time, 
            reduce vacancies, and maximize your rental income.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl p-6 text-center hover:bg-white/30 hover:border-white/40 transition-all duration-300 shadow-lg"
            >
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <feature.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
              <p className="text-sm text-slate-600">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Pricing preview */}
        <div className="bg-white/30 backdrop-blur-sm border border-white/40 rounded-2xl p-6 mb-12 max-w-3xl mx-auto">
          <h3 className="text-lg font-semibold text-slate-900 text-center mb-4">Simple, Scalable Pricing</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {pricingTiers.map((tier, index) => (
              <div key={index} className="text-center p-3 bg-white/50 rounded-xl">
                <p className="text-xs text-slate-600 mb-1">{tier.range}</p>
                <p className="text-lg font-bold text-slate-900">{tier.price}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center">
          <Button3D 
            onClick={() => navigate('/signup?type=landlord')} 
            aria-label="Get started as landlord"
            className="text-lg px-8 py-3 bg-blue-600 hover:bg-blue-800 text-white"
          >
            Get Started Free
            <ArrowRight className="ms-2 h-5 w-5" aria-hidden="true" />
          </Button3D>
          <p className="mt-4 text-sm text-slate-600">
            Start managing properties in minutes
          </p>
        </div>
      </ResponsiveContainer>
    </section>
  );
};

export default LandlordSection;
